import { useOneClickTradingV2 } from '@ui/features/OneClickTrading/hooks/useOneClickTradingV2';
import { useInvalidateLimitOrderQuery } from '@ui/features/Trade/hooks/useInvalidateQuery';
import { GenericTradeParams, TpSlValueState } from '@ui/features/Trade/types';
import { useDataChainId } from '@ui/hooks/datachain';
import { useMutationToast } from '@ui/hooks/useMutationToast';
import { useSimulateMutation } from '@ui/hooks/useSimulateMutation';
import { useTxnWriteContract } from '@ui/hooks/useTxnWriteContract';
import { pollV2IntentStatus } from '@ui/services/backend/oneClickV2Service';
import LimitOrderFacet from '@ui/services/contract/abis/LimitOrderFacet';
import { getContractAddress } from '@ui/services/contract/constants';
import { getLimitOrderFacet } from '@ui/services/contract/contract-instances/getLimitOrderFacet';
import type { Address } from '@ui/types/alias';
import { TxnMutateCallBackParams } from '@ui/types/tx';
import { extractErrorMessage } from '@ui/utils/extractContractError';
import { isNil } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { useAccount, usePublicClient } from 'wagmi';

import { useUpdateOrderTpAndSlParams } from './useUpdateOrderTpAndSlParams';
import { useUpdateOrderTpAndSlToastOpts } from './useUpdateOrderTpAndSlToastOpts';

export const useUpdateOrderTpAndSl = ({
  genericTradeParams,
  tp,
  sl,
  onSuccess,
  onError,
  onSettled,
}: {
  tp: TpSlValueState;
  sl: TpSlValueState;
} & TxnMutateCallBackParams &
  GenericTradeParams) => {
  const chainId = useDataChainId();
  const { address } = useAccount();
  const client = usePublicClient({
    chainId,
  });

  const { onErrorToastOpts, onMutateToastOpts, onSuccessToastOpts } =
    useUpdateOrderTpAndSlToastOpts({
      genericTradeParams,
    });

  const updateOrderTpAndSlParams = useUpdateOrderTpAndSlParams({
    genericTradeParams,
    tp,
    sl,
  });

  const invalidateLimitOrderQuery = useInvalidateLimitOrderQuery();

  const {
    isReady: canUseOneClickTradingV2,
    isSubmittingLimitUpdateTpSl: isSubmittingLimitUpdateTpSlV2,
    sendLimitUpdateTpSl: sendLimitUpdateTpSlV2,
  } = useOneClickTradingV2();
  const mutationToastInfo = useMutationToast();
  const [isAwaitingOneClickResult, setIsAwaitingOneClickResult] =
    useState(false);

  const { data, writeContract, isPending } = useTxnWriteContract({
    onSuccessToastOpts,
    onMutateToastOpts,
    onErrorToastOpts,
    onSuccess: () => {
      invalidateLimitOrderQuery();
      onSuccess?.();
    },
    onError,
    onSettled,
  });
  const { isSimulating, simulate } = useSimulateMutation();

  const handleOneClickUpdateOrderTpSlV2 = useCallback(async () => {
    if (!canUseOneClickTradingV2) return;
    if (isNil(updateOrderTpAndSlParams)) return;

    const [orderHash, takeProfit, stopLoss] = updateOrderTpAndSlParams;

    setIsAwaitingOneClickResult(true);
    mutationToastInfo.onMutate({
      title: 'In Progress',
      description: '⚡️ One-click V2 limit order TP/SL update in progress',
    });

    try {
      const intentHash = await sendLimitUpdateTpSlV2(
        orderHash,
        takeProfit,
        stopLoss,
      );

      const tradingStatus = await pollV2IntentStatus(intentHash);
      if (!tradingStatus.success) {
        throw new Error(
          tradingStatus.reason ||
            'One-click V2 limit order TP/SL update execution failed',
        );
      }

      invalidateLimitOrderQuery();

      mutationToastInfo.onSuccess({
        title: '⚡️ Completed',
        description:
          'Limit order TP/SL updated successfully via one-click V2',
        txHash: tradingStatus.txnHash as Address,
      });

      onSuccess?.();

      return tradingStatus.txnHash;
    } catch (error) {
      const message =
        error instanceof Error
          ? extractErrorMessage(error.message) ?? error.message
          : undefined;

      mutationToastInfo.onError({
        errorMsg: message,
        onErrorToastOpts: {
          description:
            'One-click V2 limit order TP/SL update failed, please try again',
        },
      });

      throw error;
    } finally {
      mutationToastInfo.onSettled();
      setIsAwaitingOneClickResult(false);
    }
  }, [
    canUseOneClickTradingV2,
    invalidateLimitOrderQuery,
    mutationToastInfo,
    onSuccess,
    sendLimitUpdateTpSlV2,
    updateOrderTpAndSlParams,
  ]);

  const write = useCallback(async () => {
    // OneClick V2 path (priority)
    if (canUseOneClickTradingV2) {
      return handleOneClickUpdateOrderTpSlV2();
    }

    // On-chain path
    const leverUpAddress = getContractAddress('LeverUp', chainId);
    if (
      isNil(updateOrderTpAndSlParams) ||
      isNil(leverUpAddress) ||
      isNil(client)
    )
      return;

    const { isSuccess } = await simulate(async () => {
      const contract = getLimitOrderFacet({
        chainId,
        publicClient: client,
      });

      await contract?.estimateGas.updateOrderTpAndSl(
        [...updateOrderTpAndSlParams],
        {
          account: address,
        },
      );
    });
    if (!isSuccess) {
      return;
    }

    return writeContract({
      abi: LimitOrderFacet,
      address: leverUpAddress,
      functionName: 'updateOrderTpAndSl',
      args: [...updateOrderTpAndSlParams],
    });
  }, [
    address,
    canUseOneClickTradingV2,
    chainId,
    client,
    handleOneClickUpdateOrderTpSlV2,
    simulate,
    updateOrderTpAndSlParams,
    writeContract,
  ]);

  return useMemo(() => {
    return {
      data,
      writeContract: write,
      isPending:
        isSimulating ||
        isPending ||
        isSubmittingLimitUpdateTpSlV2 ||
        isAwaitingOneClickResult,
    };
  }, [
    data,
    isPending,
    isSimulating,
    isSubmittingLimitUpdateTpSlV2,
    isAwaitingOneClickResult,
    write,
  ]);
};


